import {Injectable} from '@angular/core';
import {TenantService} from './tenant.service';
import {from, Observable} from 'rxjs';
import {AssignmentSolution} from '../models/assignment-solution.model';
import {first, map} from 'rxjs/operators';
import { createFirestoreId } from '../common/firestore-utils';
import { Firestore, QueryConstraint, collection, collectionData, doc, limit, orderBy, query, setDoc, startAfter, where, writeBatch } from '@angular/fire/firestore';


@Injectable({
  providedIn: 'root'
})
export class AssignmentSolutionsDbService {

  constructor(private tenant: TenantService,
              private firestore: Firestore) {
  }


  assignmentSolutionsPath() {
    return `schools/${this.tenant.id}/assignmentSolutions`;
  }


  createNewAssigmentSolution(solution: Partial<AssignmentSolution>): Observable<AssignmentSolution> {

    const assigmentSolutionId = createFirestoreId(this.firestore)

    const assignmentSolutionPath = `${this.assignmentSolutionsPath()}/${assigmentSolutionId}`;

    const assignmentSolutionPathDocRef = doc(this.firestore, assignmentSolutionPath)
    
    const assignmentSolutionPathAsync = setDoc(assignmentSolutionPathDocRef, solution)

    return from(assignmentSolutionPathAsync)
      .pipe(
        map(() => {
          return <AssignmentSolution>{
            id: assigmentSolutionId,
            ...solution
          };
        })
      );
  }

  /**
   *
   * A user might load several different versions of a solution for the same assignment over time.
   *
   * This query makes sure that we only get the latest uploaded solution for that user and assignment lesson,
   *
   * or null of no available solution is available.
   *
   */

  loadLatestAssignmentSolutionForLesson(lessonId: string, userId: string): Observable<AssignmentSolution> {
    
    const constraints = [
      where('userId', '==', userId),
      orderBy('submittedAt', 'desc'),
      limit(1)
    ]

    const query$ = collectionData(query(collection(this.firestore, `${this.assignmentSolutionsPath()}`), ...constraints), {idField: 'id'}).pipe(first());

    return query$
      .pipe(
        map(results => {

          if(results.length === 0) {
            return null
          }

          return <AssignmentSolution>{
            id: results[0].id,
            ...results[0]
          };

        })
      );
  }


  // loadAllAssignmentsSolution(pageSize: number, startAfterQuery?) {

  //   const constraints: QueryConstraint[] = [
  //     orderBy("submittedAt", "desc"),
  //     limit(pageSize)
  //   ]

  //   if(startAfterQuery) {
  //     constraints.push(startAfter(startAfterQuery))
  //   }

  //   const query$ = collectionData(query(collection(this.firestore, `${this.assignmentSolutionsPath()}`), ...constraints), {idField: 'id'})

  loadAssignmentsSolutionPage(pageSize: number, startAfterQuery?, status?:string) {

    const constraints: QueryConstraint[] = [
      orderBy("submittedAt", "desc"),
      limit(pageSize)
    ]
    if(startAfterQuery) {
      constraints.push(startAfter(startAfterQuery))
    }
    if (status != 'All') {
      constraints.push(where('status', '==', status));
    }

    const query$ = collectionData(query(collection(this.firestore, `${this.assignmentSolutionsPath()}`), ...constraints), {idField: 'id'}).pipe(first())

    return query$
      .pipe(
        map(results => {

          return results.map(result => {

            const data: any = result,
              id = result.id;

            return <AssignmentSolution>{
              id,
              ...data
            };

          });
        })
      );
  }

  updateAssignmentSolutionStatus(solution: AssignmentSolution, status: string) {

    const {userId, courseId, lessonId} = solution;

    const batch = writeBatch(this.firestore);

    const assignmentSolutionRef = doc(this.firestore, `${this.assignmentSolutionsPath()}/${solution.id}`);

    batch.update(assignmentSolutionRef, {status});

    if (status == "Approved") {

      const lessonStatusRef = doc(this.firestore, `schools/${this.tenant.id}/users/${userId}/lessonsWatchedPerCourse/${courseId}/lessonsWatched/${lessonId}`)

      batch.set(lessonStatusRef, {
        watched:true
      },
        {
          merge:true
        });

    }

    return batch.commit();
  }

  createNewAssignmentMessage(id: string, message: {}) {

    const assigmentMessageId = createFirestoreId(this.firestore);

    const newAssignmentMessageDocRef = doc(this.firestore, `${this.assignmentSolutionsPath()}/${id}/assignmentMessages/${assigmentMessageId}`)
    const newAssignmentMessageAsync= setDoc(newAssignmentMessageDocRef, {message});

    return newAssignmentMessageAsync

  }

}
